import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { App } from './App';
import { ProjectTakeover } from './pages/ProjectTakeover';
import { Login } from './pages/Login';
import { AuthGuard } from './components/AuthGuard';
import { TaskV2 } from './v2/pages';
import {
  V3AcceptanceDetailPage,
  V3CenterPage,
  V3PlaceholderPage,
  V3PlanDetailPage,
  V3ProgressDetailPage,
  V3ProjectIssuesPage,
  V3ProjectQualityPage,
  V3NewRequirementPage,
  V3ImportOutlinePage,
  V3RequirementDetailPage,
} from './v3/pages';
import {
  R9DeliveryRedirect,
  R9HomePage,
  R9ProjectCockpitPage,
  R9ProjectsPage,
  R9SettingsCenterPage,
  R9HumanGatePage,
  R9RepairPage,
  R9TodoPage,
} from './r9/pages';

import './styles.css';

const root = document.getElementById('root')!;

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<AuthGuard><App /></AuthGuard>}>
          <Route index element={<Navigate to="/v3/home" replace />} />
          <Route path="legacy" element={<Navigate to="/v3/home" replace />} />
          <Route path="v3" element={<Navigate to="/v3/home" replace />} />
          <Route path="v3/home" element={<R9HomePage />} />
          <Route path="v3/todo" element={<R9TodoPage />} />
          <Route path="v3/requirements" element={<V3CenterPage center="requirements" />} />
          <Route path="v3/requirements/new" element={<V3NewRequirementPage />} />
          <Route path="v3/requirements/takeover" element={<ProjectTakeover />} />
          <Route path="v3/requirements/import-outline" element={<V3ImportOutlinePage />} />
          <Route path="v3/requirements/:requirementId" element={<V3RequirementDetailPage />} />
          <Route path="v3/plans" element={<V3CenterPage center="plans" />} />
          <Route path="v3/plans/:sessionId/overview" element={<V3PlanDetailPage section="overview" />} />
          <Route path="v3/plans/:sessionId/outline" element={<V3PlanDetailPage section="outline" />} />
          <Route path="v3/plans/:sessionId/tasks" element={<V3PlanDetailPage section="tasks" />} />
          <Route path="v3/plans/:sessionId/history" element={<V3PlanDetailPage section="history" />} />
          <Route path="v3/projects" element={<R9ProjectsPage />} />
          <Route path="v3/projects/:projectId" element={<R9ProjectCockpitPage />} />
          <Route path="v3/projects/:projectId/overview" element={<R9ProjectCockpitPage tab="overview" />} />
          <Route path="v3/projects/:projectId/tasks" element={<R9ProjectCockpitPage tab="tasks" />} />
          <Route path="v3/projects/:projectId/deliverables" element={<R9ProjectCockpitPage tab="deliverables" />} />
          <Route path="v3/projects/:projectId/activity" element={<R9ProjectCockpitPage tab="activity" />} />
          <Route path="v3/projects/:projectId/issues" element={<V3ProjectIssuesPage />} />
          <Route path="v3/projects/:projectId/quality" element={<V3ProjectQualityPage />} />
          <Route path="v3/projects/:projectId/repairs" element={<R9RepairPage />} />
          <Route path="v3/projects/:projectId/stages" element={<V3PlaceholderPage kind="projectStages" />} />
          <Route path="v3/projects/:projectId/files" element={<V3PlaceholderPage kind="projectFiles" />} />
          <Route path="v3/projects/:projectId/history" element={<V3PlaceholderPage kind="projectHistory" />} />
          <Route path="v3/progress" element={<V3CenterPage center="progress" />} />
          <Route path="v3/progress/runs/:runId" element={<V3ProgressDetailPage mode="run" />} />
          <Route path="v3/progress/:projectId" element={<V3ProgressDetailPage mode="project" />} />
          <Route path="v3/acceptance" element={<V3CenterPage center="acceptance" />} />
          <Route path="v3/acceptance/tasks" element={<V3AcceptanceDetailPage kind="tasks" />} />
          <Route path="v3/acceptance/tasks/:projectId" element={<R9HumanGatePage />} />
          <Route path="v3/acceptance/stages" element={<V3AcceptanceDetailPage kind="stages" />} />
          <Route path="v3/acceptance/stages/:projectId" element={<V3AcceptanceDetailPage kind="stages" />} />
          <Route path="v3/acceptance/projects" element={<V3AcceptanceDetailPage kind="projects" />} />
          <Route path="v3/acceptance/projects/:projectId" element={<V3AcceptanceDetailPage kind="projects" />} />
          <Route path="v3/acceptance/regression" element={<V3CenterPage center="acceptance" />} />
          <Route path="v3/delivery" element={<Navigate to="/v3/projects" replace />} />
          <Route path="v3/delivery/candidates" element={<V3PlaceholderPage kind="deliveryCandidates" />} />
          <Route path="v3/delivery/official" element={<V3PlaceholderPage kind="deliveryOfficial" />} />
          <Route path="v3/delivery/:projectId" element={<R9DeliveryRedirect />} />
          <Route path="v3/delivery/:projectId/issues" element={<V3PlaceholderPage kind="deliveryIssues" />} />
          <Route path="v3/settings" element={<R9SettingsCenterPage tab="models" />} />
          <Route path="v3/settings/models" element={<R9SettingsCenterPage tab="models" />} />
          <Route path="v3/settings/routing" element={<R9SettingsCenterPage tab="models" />} />
          <Route path="v3/settings/connections" element={<R9SettingsCenterPage tab="connections" />} />
          <Route path="v3/settings/system" element={<R9SettingsCenterPage tab="diagnostics" />} />
          <Route path="v3/settings/security" element={<R9SettingsCenterPage tab="security" />} />
          <Route path="drafts" element={<Navigate to="/v3/plans" replace />} />
          <Route path="draft/:sessionId/clarify" element={<LegacySessionRedirect />} />
          <Route path="draft/:sessionId/outline" element={<LegacySessionRedirect />} />
          <Route path="draft/:sessionId/task-plan" element={<LegacySessionRedirect />} />
          <Route path="projects" element={<Navigate to="/v3/projects" replace />} />
          <Route path="project/:projectId" element={<LegacyProjectRedirect />} />
          <Route path="project/:projectId/stage/:stageId" element={<LegacyProjectRedirect tab="tasks" />} />
          <Route path="task/:taskId" element={<TaskV2 />} />
          <Route path="settings" element={<Navigate to="/v3/settings" replace />} />
          <Route path="project-takeover" element={<ProjectTakeover />} />
          <Route path="live-demo" element={<Navigate to="/v3/home" replace />} />
          <Route path="project-workbench" element={<Navigate to="/v3/projects" replace />} />
          <Route path="project-draft/:sessionId/blueprint" element={<LegacySessionRedirect />} />
          <Route path="project-draft/:sessionId/execution-plan" element={<LegacySessionRedirect />} />
          <Route path="project-draft/:sessionId" element={<LegacySessionRedirect />} />
          <Route path="project-workbench/:sessionId" element={<LegacySessionRedirect />} />
          <Route path="tasks" element={<Navigate to="/v3/projects" replace />} />
          <Route path="tasks/:taskId" element={<TaskV2 />} />
          <Route path="review" element={<Navigate to="/v3/todo" replace />} />
          <Route path="task-packages" element={<Navigate to="/v3/projects" replace />} />
          <Route path="command-center" element={<Navigate to="/v3/home" replace />} />
          <Route path="project-vault" element={<Navigate to="/v3/projects" replace />} />
          <Route path="plan" element={<Navigate to="/v3/plans" replace />} />
          <Route path="plan-ingestion" element={<Navigate to="/v3/requirements" replace />} />
          <Route path="blueprint" element={<Navigate to="/v3/plans" replace />} />
          <Route path="stage-plan" element={<Navigate to="/v3/plans" replace />} />
          <Route path="delivery-pipeline" element={<Navigate to="/v3/projects" replace />} />
          <Route path="execution" element={<Navigate to="/v3/projects" replace />} />
          <Route path="final-delivery" element={<Navigate to="/v3/projects" replace />} />
          <Route path="*" element={<Navigate to="/v3/home" replace />} />

        </Route>
      </Routes>
    </BrowserRouter>
  </React.StrictMode>,
);

function LegacySessionRedirect() {
  const { sessionId = '' } = useParams();
  return <Navigate replace to={`/v3/requirements/${sessionId}`} />;
}

function LegacyProjectRedirect({ tab }: { tab?: 'overview' | 'tasks' }) {
  const { projectId = '' } = useParams();
  return <Navigate replace to={`/v3/projects/${projectId}${tab === 'tasks' ? '/tasks' : ''}`} />;
}
